import { useState, useEffect, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import {
  Search, ExternalLink, RefreshCw, AlertTriangle, Layers, X,
  CheckSquare, Sparkles, SlidersHorizontal, ListPlus,
} from "lucide-react";
import { Popover, PopoverTrigger, PopoverContent } from "../ui/popover";
import * as api from "../../lib/api";
import {
  PRIMARY, INTENT_COLORS, FIT_COLORS, OPPORTUNITY_COLORS, fmt, pct, Th,
  usePins, PinButton, PinnedFilterButton, ExcludeButton, ExcludedFilterButton, usePersistentState,
} from "./shared";
import GroupPlanModal from "./GroupPlanModal";
import TaskPicker from "../tasks/TaskPicker";
import { TaskChip, useTaskMembers } from "../tasks/shared";

// Jev intent answers below this confidence are shown faded with a "?" — treat as a guess.
const LOW_CONFIDENCE = 0.5;

const STAGE_COLORS = {
  awareness:     { bg: "#F4F4F5", text: "#52525B" },
  consideration: { bg: "#EFF6FF", text: "#1D4ED8" },
  decision:      { bg: "#ECFDF5", text: "#065F46" },
};

function CommercialValue({ value }) {
  if (value == null) return <span className="text-zinc-300">—</span>;
  return (
    <span className="inline-flex gap-0.5" title={`Commercial value ${value}/5`}>
      {[1, 2, 3, 4, 5].map(i => (
        <span
          key={i}
          className="w-1.5 h-3 rounded-sm"
          style={{ backgroundColor: i <= value ? (value >= 4 ? "#018D4C" : "#A1A1AA") : "#E4E4E7" }}
        />
      ))}
    </span>
  );
}

const GAP_COLORS = {
  orphan:          { bg: "#FFF1F2", text: "#BE123C" },
  underperforming: { bg: "#FFFBEB", text: "#B45309" },
};

// Business-context signals from Query Gap Analysis (Settings → Content Intelligence).
function QueryContextChips({ row, topic }) {
  const lowFit = row.buyer_fit != null && row.buyer_fit < 0.5;
  if (!topic && !row.persona && !row.target_page && !row.competitor && !row.on_key_page && !lowFit) return null;
  const chip = "px-1.5 py-0.5 text-[9px] font-bold rounded";
  return (
    <div className="flex flex-wrap gap-1 mt-1">
      {topic && <span className={`${chip} bg-violet-50 text-violet-700 max-w-[180px] truncate`} title={`Topic cluster: ${topic}`}>#{topic}</span>}
      {row.persona && <span className={`${chip} bg-green-50 text-green-700`} title={`Buyer fit ${Math.round(row.buyer_fit * 100)}%`}>{row.persona}</span>}
      {lowFit && <span className={`${chip} bg-zinc-100 text-zinc-500`} title={`Buyer fit ${Math.round(row.buyer_fit * 100)}%`}>not our buyer</span>}
      {row.on_key_page && <span className={`${chip} bg-emerald-50 text-emerald-700`}>key page</span>}
      {row.target_page && !row.on_key_page && (
        <span className={`${chip} bg-blue-50 text-blue-700 max-w-[220px] truncate`} title={`Key page that should rank: ${row.target_page}`}>
          → {row.target_page.replace(/^https?:\/\/[^/]+/, "") || "/"}
        </span>
      )}
      {row.competitor && <span className={`${chip} bg-amber-50 text-amber-700`}>vs {row.competitor}</span>}
    </div>
  );
}

export default function QueriesTab({ daysBack = 28, refreshing = false, onAnalyzingChange }) {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [source, setSource] = useState(null);
  const [sort, setSort] = usePersistentState("queries.sort", "clicks");
  const [order, setOrder] = usePersistentState("queries.order", "desc");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [gapFilter, setGapFilter] = usePersistentState("queries.gapFilter", "");
  const [intentFilter, setIntentFilter] = usePersistentState("queries.intentFilter", "");
  const [fitFilter, setFitFilter] = usePersistentState("queries.fitFilter", "");
  const [opportunityFilter, setOpportunityFilter] = usePersistentState("queries.opportunityFilter", "");
  const [stageFilter, setStageFilter] = usePersistentState("queries.stageFilter", "");
  const [topicFilter, setTopicFilter] = usePersistentState("queries.topicFilter", "");
  // "" | any (in an open task) | none (in no open task — the inbox) | SEO-<n>; Tasks → "View in Search Performance" presets it.
  const [taskFilter, setTaskFilter] = usePersistentState("queries.taskFilter", "");
  const { members: taskMembers, canEdit: canEditTasks } = useTaskMembers();
  // Rows the task dialog acts on: one row (row icon) or the selection (selection bar); null = closed.
  const [taskPickerRows, setTaskPickerRows] = useState(null);
  const [taskNotice, setTaskNotice] = useState("");
  // cluster_id → label, so query rows link to the same topics as the Clusters page.
  const [topics, setTopics] = useState({});
  const [pinnedOnly, setPinnedOnly] = usePersistentState("queries.pinnedOnly", false);
  const pins = usePins("query");
  const [excludedOnly, setExcludedOnly] = usePersistentState("queries.excludedOnly", false);
  const excludes = usePins("exclude");
  const [offset, setOffset] = useState(0);
  const [selected, setSelected] = useState(new Set());
  const [grouping, setGrouping] = useState(false);
  const [groups, setGroups] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [analyzeMsg, setAnalyzeMsg] = useState("");
  const [analyzeError, setAnalyzeError] = useState("");
  const [analyzeDone, setAnalyzeDone] = useState("");
  const [analyzeReportId, setAnalyzeReportId] = useState(null);
  const analyzePollRef = useRef(null);
  const tableScrollRef = useRef(null);
  const [moreColumnsRight, setMoreColumnsRight] = useState(false);
  const [diagnosingId, setDiagnosingId] = useState(null);
  const LIMIT = 100;

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 350);
    return () => clearTimeout(t);
  }, [search]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { sort, order, limit: LIMIT, offset };
      if (debouncedSearch) params.search = debouncedSearch;
      if (gapFilter) params.gap_type = gapFilter;
      if (intentFilter) params.intent = intentFilter;
      if (fitFilter) params.page_fit = fitFilter;
      if (opportunityFilter) params.opportunity = opportunityFilter;
      if (stageFilter) params.funnel_stage = stageFilter;
      if (topicFilter) params.cluster_id = topicFilter;
      if (taskFilter) params.task = taskFilter;
      if (pinnedOnly) params.pinned = true;
      if (excludedOnly) params.excluded = true;
      const r = await api.getPerformanceQueries(params);
      setRows(r.queries || []);
      setTotal(r.total || 0);
      setSource(r.source || null);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [sort, order, debouncedSearch, gapFilter, intentFilter, fitFilter, opportunityFilter, stageFilter, topicFilter, taskFilter, pinnedOnly, excludedOnly, offset]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    api.getClusters()
      .then(d => setTopics(Object.fromEntries((d.clusters || []).map(c => [c.cluster_id, c.label || c.cluster_id]))))
      .catch(() => {});
  }, []);

  const handleSort = (col) => {
    if (sort === col) {
      setOrder(o => o === "desc" ? "asc" : "desc");
    } else {
      setSort(col);
      setOrder("desc");
    }
    setOffset(0);
    setSelected(new Set());
  };

  const toggleRow = (id) => setSelected(s => {
    const next = new Set(s);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const pageIds = rows.map(r => r.id).filter(Boolean);
  const allPageSelected = pageIds.length > 0 && pageIds.every(id => selected.has(id));
  const somePageSelected = pageIds.some(id => selected.has(id));

  const togglePage = () => {
    if (allPageSelected) {
      setSelected(s => { const n = new Set(s); pageIds.forEach(id => n.delete(id)); return n; });
    } else {
      setSelected(s => { const n = new Set(s); pageIds.forEach(id => n.add(id)); return n; });
    }
  };

  const handleGroupPlan = async () => {
    if (selected.size === 0) return;
    setGrouping(true);
    try {
      const r = await api.groupGscQueries([...selected]);
      setGroups(r.groups || []);
    } catch (e) {
      console.error(e);
    } finally {
      setGrouping(false);
    }
  };

  const isRefreshSource = source === "refresh";

  const applyFilter = (set, value) => { set(value); setOffset(0); setSelected(new Set()); };
  const humanize = v => v.replace(/_/g, " ");
  const optionsOf = colors => Object.keys(colors).map(v => ({ value: v, label: humanize(v) }));
  // Secondary filters live in the Filters popover; set ones also render as removable chips.
  const facets = [
    { key: "intent",      label: "Intent",      value: intentFilter,      set: setIntentFilter,      options: optionsOf(INTENT_COLORS) },
    { key: "fit",         label: "Page fit",    value: fitFilter,         set: setFitFilter,         options: optionsOf(FIT_COLORS) },
    { key: "opportunity", label: "Opportunity", value: opportunityFilter, set: setOpportunityFilter, options: optionsOf(OPPORTUNITY_COLORS) },
    { key: "stage",       label: "Stage",       value: stageFilter,       set: setStageFilter,       options: optionsOf(STAGE_COLORS) },
    {
      key: "topic", label: "Topic", value: topicFilter, set: setTopicFilter,
      options: [
        { value: "none", label: "No topic" },
        ...Object.entries(topics).sort((a, b) => a[1].localeCompare(b[1])).map(([id, label]) => ({ value: id, label })),
      ],
    },
    {
      key: "task", label: "Task", value: taskFilter, set: setTaskFilter,
      options: [
        { value: "none", label: "Not in a task" },
        { value: "any", label: "In a task" },
        ...(/^SEO-\d+$/i.test(taskFilter) ? [{ value: taskFilter, label: taskFilter.toUpperCase() }] : []),
      ],
    },
  ];
  // Topic can be preset from the Topic Clusters tab, so its chip shows even before topics load.
  const activeFacets = isRefreshSource ? [] : facets.filter(f => f.value);
  const clearFacets = () => {
    facets.forEach(f => f.set(""));
    setOffset(0);
    setSelected(new Set());
  };

  const handleDiagnoseFit = async (row) => {
    setDiagnosingId(row.id);
    try {
      const r = await api.analyzeQueryPage(row.id);
      setRows(rs => rs.map(x => x.id === row.id
        ? { ...x, page_fit: r.page_fit, page_fit_confidence: r.page_fit_confidence }
        : x));
    } catch (e) {
      console.error(e);
    } finally {
      setDiagnosingId(null);
    }
  };

  // Shift+scroll hint: only while the table overflows and the right edge isn't reached.
  useEffect(() => {
    const el = tableScrollRef.current;
    if (!el) return;
    const update = () => setMoreColumnsRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
    update();
    el.addEventListener("scroll", update, { passive: true });
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => { el.removeEventListener("scroll", update); ro.disconnect(); };
  }, [rows]);

  useEffect(() => () => { if (analyzePollRef.current) clearInterval(analyzePollRef.current); }, []);

  // Page-level Refresh and Run Analysis rewrite the same table — lock each while the other runs.
  // Release the lock if the tab unmounts mid-run; the backend 409 still guards the overlap.
  useEffect(() => { onAnalyzingChange?.(analyzing); }, [analyzing, onAnalyzingChange]);
  useEffect(() => () => onAnalyzingChange?.(false), [onAnalyzingChange]);

  const handleRunAnalysis = async () => {
    setAnalyzing(true);
    setAnalyzeError("");
    setAnalyzeDone("");
    setAnalyzeReportId(null);
    setAnalyzeMsg("Analyzing…");
    try {
      const res = await api.runQueryGapAnalysis(daysBack);
      let state = res.state || { status: "running" };
      analyzePollRef.current = setInterval(async () => {
        try {
          const poll = await api.getQueryGaps();
          state = poll.state || { status: "idle" };
          if (state.status !== "running") {
            clearInterval(analyzePollRef.current);
            analyzePollRef.current = null;
            setAnalyzing(false);
            setAnalyzeMsg("");
            if (state.status === "failed") {
              setAnalyzeError(state.message || state.error || "Analysis failed.");
            } else if (state.counts) {
              const c = state.counts;
              setAnalyzeReportId(c.report_id || null);
              setAnalyzeDone(
                `${fmt(c.total)} queries analyzed · ${fmt(c.orphan)} orphan · ${fmt(c.underperforming)} underperforming · classified ${fmt(c.classified)} new`
                + (c.carried_over ? ` + ${fmt(c.carried_over)} kept from earlier runs` : "")
                + (c.remaining ? ` · ${fmt(c.remaining)} still unclassified (run again to classify the next batch)` : "")
                + (c.excluded ? ` · ${fmt(c.excluded)} excluded` : "")
                + (c.classify_failed ? ` · ${fmt(c.classify_failed)} failed (left blank, retried next run)` : "")
              );
            }
            load();
          } else {
            setAnalyzeMsg(state.message || "Analyzing…");
          }
        } catch {
          clearInterval(analyzePollRef.current);
          analyzePollRef.current = null;
          setAnalyzing(false);
          setAnalyzeMsg("");
          setAnalyzeError("Lost contact with the server while polling — check backend logs.");
        }
      }, 3000);
    } catch (e) {
      console.error(e);
      const detail = e?.response?.data?.detail;
      setAnalyzing(false);
      setAnalyzeMsg("");
      setAnalyzeError(
        Array.isArray(detail) ? detail.map(d => d.msg).join("; ")
        : detail || e?.message || "Analysis failed to start."
      );
    }
  };

  return (
    // Room under the table so the fixed selection bar never covers the last rows or pagination.
    <div className={selected.size > 0 ? "pb-20" : ""}>
      {analyzeError && (
        <div className="flex items-center gap-2 px-4 py-2.5 mb-3 bg-red-50 border border-red-100 rounded-xl text-xs text-red-700">
          <AlertTriangle size={13} className="flex-shrink-0" />
          Run Analysis failed: {analyzeError}
          <button onClick={() => setAnalyzeError("")} className="ml-auto text-red-400 hover:text-red-700"><X size={12} /></button>
        </div>
      )}
      {analyzeDone && (
        <div className="flex items-center gap-2 px-4 py-2.5 mb-3 bg-emerald-50 border border-emerald-100 rounded-xl text-xs text-emerald-800">
          <CheckSquare size={13} className="flex-shrink-0" />
          Analysis complete: {analyzeDone}
          {analyzeReportId && (
            <Link to={`/dashboard/reports/${analyzeReportId}`} className="font-bold underline whitespace-nowrap">
              View before/after report
            </Link>
          )}
          <button onClick={() => setAnalyzeDone("")} className="ml-auto text-emerald-400 hover:text-emerald-700"><X size={12} /></button>
        </div>
      )}
      {isRefreshSource && (
        <div className="flex items-center gap-2 px-4 py-2.5 mb-3 bg-zinc-50 border border-zinc-100 rounded-xl text-xs text-zinc-500">
          <AlertTriangle size={13} className="text-zinc-400 flex-shrink-0" />
          Showing query data from last GSC Refresh (no gap type or intent).
          Run <a href="/dashboard/query-gaps" className="font-bold underline text-zinc-700">Query Gap Analysis</a> for full classification.
        </div>
      )}
      <div className={`flex flex-wrap items-center gap-2 ${activeFacets.length ? "mb-2" : "mb-4"}`}>
        <div className="relative flex-1 min-w-[160px] max-w-xs">
          <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <input
            value={search}
            onChange={e => { setSearch(e.target.value); setOffset(0); setSelected(new Set()); }}
            placeholder="Search queries..."
            className="w-full pl-8 pr-3 py-1.5 text-xs border border-zinc-100 bg-white rounded-lg focus:outline-none focus:ring-2 placeholder-zinc-400"
            style={{ "--tw-ring-color": PRIMARY }}
          />
        </div>
        {!isRefreshSource && (
          <div className="flex gap-0.5 bg-zinc-100 rounded-lg p-0.5">
            {["", "orphan", "underperforming"].map(v => (
              <button
                key={v}
                onClick={() => applyFilter(setGapFilter, v)}
                className="px-2.5 py-1 text-[10px] tracking-[0.1em] uppercase font-bold rounded-md transition-colors"
                style={gapFilter === v
                  ? { backgroundColor: PRIMARY, color: "#fff" }
                  : { backgroundColor: "transparent", color: "#71717a" }}
              >
                {v || "All"}
              </button>
            ))}
          </div>
        )}
        {!isRefreshSource && (
          <Popover>
            <PopoverTrigger asChild>
              <button
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[10px] tracking-[0.1em] uppercase font-bold rounded-lg transition-colors border"
                style={activeFacets.length
                  ? { background: "#ECFDF5", color: PRIMARY, borderColor: PRIMARY }
                  : { background: "#fff", color: "#71717a", borderColor: "#f4f4f5" }}
              >
                <SlidersHorizontal size={11} />
                Filters
                {activeFacets.length > 0 && (
                  <span className="min-w-[16px] h-4 px-1 rounded-full text-[9px] leading-4 text-center text-white" style={{ backgroundColor: PRIMARY }}>
                    {activeFacets.length}
                  </span>
                )}
              </button>
            </PopoverTrigger>
            <PopoverContent align="start" className="w-64 p-3">
              <div className="space-y-2.5">
                {facets.map(f => (
                  <label key={f.key} className="block">
                    <span className="block mb-1 text-[10px] tracking-[0.1em] uppercase font-bold text-zinc-400">{f.label}</span>
                    <select
                      value={f.value}
                      onChange={e => applyFilter(f.set, e.target.value)}
                      className="w-full px-2.5 py-1.5 text-xs rounded-lg border border-zinc-100 bg-white text-zinc-700 focus:outline-none focus:ring-2"
                      style={{ "--tw-ring-color": PRIMARY }}
                    >
                      <option value="">Any</option>
                      {f.options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                  </label>
                ))}
              </div>
              {activeFacets.length > 0 && (
                <button
                  onClick={clearFacets}
                  className="mt-3 text-[11px] font-bold text-zinc-400 hover:text-zinc-700"
                >
                  Clear all filters
                </button>
              )}
            </PopoverContent>
          </Popover>
        )}
        <PinnedFilterButton
          compact
          active={pinnedOnly}
          count={pins.count}
          onClick={() => { setPinnedOnly(v => !v); setOffset(0); setSelected(new Set()); }}
        />
        <ExcludedFilterButton
          compact
          active={excludedOnly}
          count={excludes.count}
          onClick={() => { setExcludedOnly(v => !v); setOffset(0); setSelected(new Set()); }}
        />
        <div className="flex items-center gap-2 ml-auto">
          <span className="text-xs text-zinc-400 whitespace-nowrap">{fmt(total)} queries</span>
          <button
            onClick={load}
            title="Reload rows"
            className="p-2 text-zinc-400 hover:text-zinc-700 transition-colors"
          >
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
          </button>
          <button
            onClick={handleRunAnalysis}
            disabled={analyzing || refreshing}
            title={refreshing ? "Wait for GSC Refresh to finish" : "Run Query Gap Analysis — classifies gap_type + intent"}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-lg disabled:opacity-50 transition-colors whitespace-nowrap"
            style={{ backgroundColor: PRIMARY, color: "#fff" }}
          >
            <Sparkles size={12} className={analyzing ? "animate-spin" : ""} />
            {analyzing ? (analyzeMsg || "Analyzing…") : "Run Analysis"}
          </button>
        </div>
      </div>

      {activeFacets.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5 mb-4">
          {activeFacets.map(f => (
            <span
              key={f.key}
              className="inline-flex items-center gap-1 pl-2.5 pr-1 py-0.5 text-[11px] rounded-full border border-zinc-200 bg-white text-zinc-600"
            >
              <span className="text-zinc-400">{f.label}:</span>
              <span className="font-bold">{f.options.find(o => o.value === f.value)?.label ?? f.value}</span>
              <button
                onClick={() => applyFilter(f.set, "")}
                title={`Remove ${f.label.toLowerCase()} filter`}
                className="p-0.5 rounded-full text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100"
              >
                <X size={10} />
              </button>
            </span>
          ))}
          <button onClick={clearFacets} className="px-1.5 text-[11px] font-bold text-zinc-400 hover:text-zinc-700">
            Clear all
          </button>
        </div>
      )}

      {taskNotice && (
        <div className="flex items-center gap-2 px-4 py-2.5 mb-3 bg-emerald-50 border border-emerald-100 rounded-xl text-xs text-emerald-800">
          <CheckSquare size={13} className="flex-shrink-0" />
          {taskNotice}
          <Link to="/dashboard/tasks" className="font-bold underline">open Tasks</Link>
          <button onClick={() => setTaskNotice("")} className="ml-auto text-emerald-400 hover:text-emerald-700"><X size={12} /></button>
        </div>
      )}

      {/* Floating action bar */}
      {selected.size > 0 && (
        // Fixed to the viewport bottom so bulk actions stay reachable while scrolling the table.
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 w-[min(720px,calc(100vw-2rem))] flex items-center gap-3 px-4 py-2.5 bg-zinc-900 text-white rounded-xl text-sm shadow-2xl">
          <CheckSquare size={14} className="flex-shrink-0" style={{ color: PRIMARY }} />
          <span className="font-bold">{selected.size} selected</span>
          <div className="flex-1" />
          <button
            onClick={() => setTaskPickerRows(rows.filter(r => selected.has(r.id)))}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-lg bg-white/10 hover:bg-white/20"
          >
            <ListPlus size={12} />
            Create / add to task
          </button>
          <button
            onClick={handleGroupPlan}
            disabled={grouping}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-lg disabled:opacity-40"
            style={{ backgroundColor: PRIMARY }}
          >
            <Layers size={12} className={grouping ? "animate-spin" : ""} />
            {grouping ? "Grouping…" : "Group & Plan"}
          </button>
          <button
            onClick={() => setSelected(new Set())}
            className="p-1.5 rounded-lg hover:bg-white/10 text-zinc-400 hover:text-white"
          >
            <X size={14} />
          </button>
        </div>
      )}

      {moreColumnsRight && (
        <p className="flex justify-end items-center gap-1 mb-1.5 text-[11px] text-zinc-400">
          Hold
          <kbd className="px-1.5 py-px rounded border border-zinc-200 bg-white font-sans text-[10px] font-bold text-zinc-500">Shift</kbd>
          + scroll to see more columns →
        </p>
      )}
      <div className="bg-white rounded-xl border border-zinc-100 shadow-sm overflow-hidden">
        <div ref={tableScrollRef} className="overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead className="border-b border-zinc-100 bg-zinc-50">
              <tr>
                <th className="px-3 py-2.5 w-10 sticky left-0 z-10 bg-zinc-50">
                  <input
                    type="checkbox"
                    checked={allPageSelected}
                    ref={el => { if (el) el.indeterminate = somePageSelected && !allPageSelected; }}
                    onChange={togglePage}
                    className="rounded border-zinc-300 cursor-pointer"
                  />
                </th>
                <th className="px-3 py-2 text-left text-[9px] tracking-[0.12em] uppercase font-bold text-zinc-400 min-w-[240px] sticky left-10 z-10 bg-zinc-50 shadow-[1px_0_0_#f4f4f5]">Query / ranking page</th>
                <Th col="clicks"      label="Clicks"      sort={sort} order={order} onSort={handleSort} />
                <Th col="impressions" label="Impressions" sort={sort} order={order} onSort={handleSort} />
                <Th col="ctr"         label="CTR"         sort={sort} order={order} onSort={handleSort} />
                <Th col="position"    label="Position"    sort={sort} order={order} onSort={handleSort} />
                <Th col="commercial_value" label="Value" sort={sort} order={order} onSort={handleSort} />
                <th className="px-3 py-2 text-left text-[9px] tracking-[0.12em] uppercase font-bold text-zinc-400">Intent · Stage</th>
                <th className="px-3 py-2 text-left text-[9px] tracking-[0.12em] uppercase font-bold text-zinc-400">Fit</th>
                <th className="px-3 py-2 text-left text-[9px] tracking-[0.12em] uppercase font-bold text-zinc-400">Opportunity · Type</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-50">
              {loading && (
                <tr><td colSpan={10} className="px-4 py-8 text-center text-sm text-zinc-400">Loading...</td></tr>
              )}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={10} className="px-4 py-8 text-center text-sm text-zinc-400">
                  No query data yet. Click Refresh (top right) to pull data from Search Console, then Run Analysis to classify it.
                </td></tr>
              )}
              {!loading && rows.map(row => {
                const gc = GAP_COLORS[row.gap_type] || { bg: "#F4F4F5", text: "#52525B" };
                const ic = INTENT_COLORS[row.intent] || { bg: "#F4F4F5", text: "#52525B" };
                const fc = FIT_COLORS[row.page_fit] || { bg: "#F4F4F5", text: "#52525B" };
                const oc = OPPORTUNITY_COLORS[row.opportunity] || { bg: "#F4F4F5", text: "#52525B" };
                const isSelected = selected.has(row.id);
                // Sticky cells need an opaque background or scrolled columns show through.
                const stickyBg = isSelected ? "bg-green-50" : "bg-white group-hover:bg-zinc-50";
                return (
                  <tr
                    key={row.id}
                    className={`group transition-colors ${isSelected ? "bg-green-50/60" : "hover:bg-zinc-50/50"}`}
                  >
                    <td className={`px-3 py-2 w-10 sticky left-0 z-[1] ${stickyBg}`}>
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleRow(row.id)}
                        className="rounded border-zinc-300 cursor-pointer"
                      />
                    </td>
                    <td className={`px-3 py-2 min-w-[240px] max-w-[360px] sticky left-10 z-[1] shadow-[1px_0_0_#f4f4f5] ${stickyBg}`}>
                      <div className="flex items-start gap-1">
                        <PinButton pinned={pins.isPinned(row.query)} onClick={() => pins.toggle(row.query)} />
                        <ExcludeButton excluded={excludes.isPinned(row.query)} onClick={() => excludes.toggle(row.query)} />
                        <button
                          onClick={() => setTaskPickerRows([row])}
                          title="Create task or add to task"
                          className="p-1 rounded text-zinc-300 hover:text-zinc-600 transition-colors"
                        >
                          <ListPlus size={13} />
                        </button>
                        <div className="min-w-0">
                          <p className="font-medium text-zinc-900 line-clamp-2 break-words" title={row.query}>{row.query}</p>
                          {row.url ? (
                            <a
                              href={row.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              title={row.url}
                              className="flex items-center gap-1 mt-0.5 text-[10px] text-zinc-400 hover:text-zinc-800 min-w-0"
                            >
                              <ExternalLink size={9} className="flex-shrink-0" />
                              <span className="truncate">{row.url.replace(/^https?:\/\/[^/]+/, "") || "/"}</span>
                            </a>
                          ) : <span className="block mt-0.5 text-[10px] text-zinc-300">no page</span>}
                          <QueryContextChips row={row} topic={row.cluster_id ? topics[row.cluster_id] || row.cluster_id : null} />
                          {row.task_number != null && (
                            <Link to={`/dashboard/tasks/SEO-${row.task_number}`} className="inline-block mt-1 hover:opacity-80" title="Open task">
                              <TaskChip number={row.task_number} status={row.task_status} />
                            </Link>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-2 font-bold text-zinc-900 tabular-nums">{fmt(row.clicks)}</td>
                    <td className="px-3 py-2 text-zinc-700 tabular-nums">{fmt(row.impressions)}</td>
                    <td className="px-3 py-2 text-zinc-700 tabular-nums">{pct(row.ctr)}</td>
                    <td className="px-3 py-2 tabular-nums">
                      {row.position != null ? (
                        <span className={`font-bold ${
                          row.position <= 3 ? "text-green-600"
                          : row.position <= 10 ? "text-amber-600"
                          : "text-red-500"
                        }`}>
                          {fmt(row.position, 1)}
                        </span>
                      ) : <span className="text-zinc-400">—</span>}
                    </td>
                    <td className="px-3 py-2">
                      <CommercialValue value={row.commercial_value} />
                    </td>
                    <td className="px-3 py-2">
                      {row.intent && (
                        <span
                          className={`px-2 py-0.5 text-[9px] tracking-[0.1em] font-bold uppercase rounded-full ${
                            row.confidence != null && row.confidence < LOW_CONFIDENCE ? "opacity-50" : ""
                          }`}
                          style={{ background: ic.bg, color: ic.text }}
                          title={row.confidence != null ? `Confidence ${Math.round(row.confidence * 100)}%` : undefined}
                        >
                          {row.intent.replace(/_/g, " ")}
                          {row.confidence != null && row.confidence < LOW_CONFIDENCE && " ?"}
                        </span>
                      )}
                      {!row.intent && excludes.isPinned(row.query) && (
                        <span className="text-[9px] tracking-[0.1em] font-bold uppercase text-zinc-400">excluded</span>
                      )}
                      {row.funnel_stage && (
                        <span
                          className="block mt-1 text-[10px] font-medium"
                          style={{ color: STAGE_COLORS[row.funnel_stage]?.text ?? "#71717a" }}
                        >
                          {row.funnel_stage}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      {row.page_fit ? (
                        <span
                          className="px-2 py-0.5 text-[9px] tracking-[0.1em] font-bold uppercase rounded-full"
                          style={{ background: fc.bg, color: fc.text }}
                        >
                          {row.page_fit.replace(/_/g, " ")}
                        </span>
                      ) : row.url ? (
                        <button
                          onClick={() => handleDiagnoseFit(row)}
                          disabled={diagnosingId === row.id}
                          className="text-[9px] tracking-[0.1em] uppercase font-bold text-zinc-400 hover:text-zinc-700 disabled:opacity-50 underline decoration-dotted"
                        >
                          {diagnosingId === row.id ? "Diagnosing…" : "Diagnose"}
                        </button>
                      ) : null}
                    </td>
                    <td className="px-3 py-2">
                      {row.opportunity && (
                        <span
                          className="px-2 py-0.5 text-[9px] tracking-[0.1em] font-bold uppercase rounded-full"
                          style={{ background: oc.bg, color: oc.text }}
                        >
                          {row.opportunity.replace(/_/g, " ")}
                        </span>
                      )}
                      {row.gap_type ? (
                        <span className="block mt-1 text-[10px] font-medium" style={{ color: gc.text }}>
                          {row.gap_type}
                        </span>
                      ) : row.position != null && row.position <= 10 && (
                        <span className="block mt-1 text-[10px] font-medium text-zinc-400" title="Already ranks on page 1 — nothing to fix">
                          ranking
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {total > LIMIT && (
        <div className="flex items-center justify-between mt-3 text-xs text-zinc-500">
          <span>Showing {offset + 1}–{Math.min(offset + LIMIT, total)} of {total}</span>
          <div className="flex gap-2">
            <button
              disabled={offset === 0}
              onClick={() => { setOffset(o => Math.max(0, o - LIMIT)); setSelected(new Set()); }}
              className="px-3 py-1.5 border border-zinc-100 rounded-lg disabled:opacity-40 hover:bg-zinc-50"
            >Previous</button>
            <button
              disabled={offset + LIMIT >= total}
              onClick={() => { setOffset(o => o + LIMIT); setSelected(new Set()); }}
              className="px-3 py-1.5 border border-zinc-100 rounded-lg disabled:opacity-40 hover:bg-zinc-50"
            >Next</button>
          </div>
        </div>
      )}

      <TaskPicker
        open={taskPickerRows !== null}
        onOpenChange={(o) => { if (!o) setTaskPickerRows(null); }}
        rows={taskPickerRows || []}
        members={taskMembers}
        canAddToExisting={canEditTasks}
        onDone={(task, message) => { setTaskNotice(`${message} — `); setSelected(new Set()); load(); }}
      />

      {groups && (
        <GroupPlanModal
          groups={groups}
          onClose={() => setGroups(null)}
          onAdded={() => setSelected(new Set())}
        />
      )}
    </div>
  );
}
